import { purchaseOrderStatusEnums } from '@/types/enums';
import dayjs from 'dayjs';
import z from 'zod';

const purchaseOrderStatusSchema = z.enum(
  purchaseOrderStatusEnums?.map((status) => status?.value),
);

const purchaseOrderLineResponseSchema = z.object({
  id: z.number(),
  created_at: z.date(),
  updated_at: z.date(),
  qty_ordered: z.number(),
  qty_received: z.number().optional(),
  unit_price: z.string().nullable(),
  needed_by: z.date().nullable(),
  promised_date: z.date().nullable().optional(),
  received_at: z.date().nullable().optional(),
  notes: z.string().nullable(),
  part_number_snapshot: z.string(),
  part_description_snapshot: z.string().nullable(),
  manual_part_number: z.string().nullable().optional(),
  po: z.number(),
  part: z.number().nullable(),
});

export const purchaseOrderResponseSchema = z.object({
  id: z.number(),
  release_numbers: z.array(z.string()),
  lines: z.array(purchaseOrderLineResponseSchema),
  created_at: z.date(),
  updated_at: z.date(),
  project_po_number: z.string().nullable(),
  po_number: z.string(),
  title: z.string().nullable(),
  function_number: z.string().nullable(),
  status: purchaseOrderStatusSchema,
  issued_at: z.date().nullable(),
  expected_delivery_date: z.date().nullable(),
  received_date: z.string().nullable().optional(),
  received_by: z.number().nullable().optional(),
  received_by_name: z.string().nullable().optional(),
  received_notes: z.string().nullable().optional(),
  shipping_address: z.string().nullable(),
  supplier_reference: z.string().nullable(),
  project: z.number(),
  supplier: z.number(),
  created_by: z.number().nullable(),
  source_draft: z.number().nullable(),
  release_ids: z.array(z.number()).optional(),
});

const nullableDateTimeSchema = z
  .string()
  .refine((value) => dayjs(value).isValid(), {
    error: 'Select a valid date.',
  })
  .nullable()
  .optional();

const directPurchaseOrderLineCommon = {
  qty: z.coerce.number().int().positive(),
  unit_price: z
    .union([z.string(), z.number()])
    .transform((value) => (typeof value === 'number' ? String(value) : value))
    .nullable()
    .optional(),
  needed_by: nullableDateTimeSchema,
  promised_date: nullableDateTimeSchema,
  notes: z.string().nullable().optional(),
};

const directManualPurchaseOrderLineSchema = z
  .object({
    ...directPurchaseOrderLineCommon,
    manual_part_number: z.string().trim().min(1),
    description: z.string().nullable().optional(),
    part: z.never().optional(),
    release: z.never().optional(),
  })
  .strict();

const directLinkedPurchaseOrderLineSchema = z
  .object({
    ...directPurchaseOrderLineCommon,
    part: z.coerce.number().int().positive(),
    release: z.coerce.number().int().positive().nullable().optional(),
    manual_part_number: z.never().optional(),
    description: z.never().optional(),
  })
  .strict();

export const postDirectPurchaseOrder = {
  body: z.object({
    project: z.coerce.number().int().positive(),
    supplier: z.coerce.number().int().positive(),
    title: z.string().nullable().optional(),
    project_po_number: z.string().nullable().optional(),
    po_number: z.string().nullable().optional(),
    function_number: z.string().nullable().optional(),
    expected_delivery_date: nullableDateTimeSchema,
    shipping_address: z.string().nullable().optional(),
    supplier_reference: z.string().nullable().optional(),
    lines: z
      .array(
        z.union([
          directManualPurchaseOrderLineSchema,
          directLinkedPurchaseOrderLineSchema,
        ]),
      )
      .min(1),
  }),
  response: purchaseOrderResponseSchema,
  params: z.never(),
  query: z.never(),
};
const postDirectPurchaseOrderSchema = z.object(postDirectPurchaseOrder);
export type PostDirectPurchaseOrder = z.infer<
  typeof postDirectPurchaseOrderSchema
>;

export const postPurchaseOrder = {
  body: z.object({
    expected_delivery_date: z
      .string()
      .nonempty({ error: 'Expected delivery date is required' })
      .refine((val) => dayjs(val).isValid(), { error: 'Select a valid date.' }), // "expected_delivery_date": "2026-02-12"
    po_number: z.string().optional(),
    project_po_number: z.string().nullable().optional(),
  }),
  response: z.object({
    po_id: z.number(),
    project_po_number: z.string().nullable(),
    po_number: z.string(),
    title: z.string().nullable(),
  }),
  params: z.object({
    draft_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postPurchaseOrderSchema = z.object(postPurchaseOrder);
export type PostPurchaseOrder = z.infer<typeof postPurchaseOrderSchema>;

const getAllPurchaseOrders = {
  body: z.never(),
  response: z.array(purchaseOrderResponseSchema),
  params: z.never(),
  query: z
    .object({
      project_id: z.number().optional(),
      supplier_id: z.number().optional(),
      status: purchaseOrderStatusSchema.optional(),
      open_only: z.boolean().optional(),
      cancelled: z.boolean().optional(),
    })
    .optional(),
};
const getAllPurchaseOrdersSchema = z.object(getAllPurchaseOrders);
export type GetAllPurchaseOrders = z.infer<typeof getAllPurchaseOrdersSchema>;

const putPurchaseOrder = {
  body: z.object({
    po_number: z.string(),
    title: z.string().nullable(),
    function_number: z.string().nullable(),
    status: purchaseOrderStatusSchema,
    issued_at: z.string().refine((val) => dayjs(val).isValid()),
    expected_delivery_date: z.string().refine((val) => dayjs(val).isValid()),
    shipping_address: z.string(),
    supplier_reference: z.string(),
    supplier: z.number(),
    created_by: z.number(),
    source_draft: z.number(),
  }),
  response: purchaseOrderResponseSchema,
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const putPurchaseOrderSchema = z.object(putPurchaseOrder);
export type PutPurchaseOrder = z.infer<typeof putPurchaseOrderSchema>;

export const patchPurchaseOrder = {
  body: z
    .object({
      po_number: z.string(),
      project_po_number: z.string(),
      title: z.string().nullable(),
      function_number: z.string().nullable(),
      status: purchaseOrderStatusSchema,
      issued_at: z.string().refine((val) => dayjs(val).isValid()),
      expected_delivery_date: z.string().nullable(),
      shipping_address: z.string().nullable(),
      supplier_reference: z.string().nullable(),
      supplier: z.number(),
      created_by: z.number(),
      source_draft: z.number(),
    })
    .partial(),
  response: purchaseOrderResponseSchema,
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchPurchaseOrderSchema = z.object(patchPurchaseOrder);
export type PatchPurchaseOrder = z.infer<typeof patchPurchaseOrderSchema>;

export const getPurchaseOrder = {
  body: z.never(),
  response: purchaseOrderResponseSchema,
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const getPurchaseOrderSchema = z.object(getPurchaseOrder);
export type GetPurchaseOrder = z.infer<typeof getPurchaseOrderSchema>;

const postCancel = {
  body: z.never(),
  response: z.object({
    detail: z.string(),
    po_id: z.number(),
    project_po_number: z.string().nullable(),
    po_number: z.string(),
    title: z.string().nullable(),
  }),
  params: z.object({
    po_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postCancelSchema = z.object(postCancel);
export type PostCancel = z.infer<typeof postCancelSchema>;

export const postRestore = {
  body: z.never(),
  response: z.object({
    detail: z.string(),
    po_id: z.number(),
    project_po_number: z.string().nullable(),
    po_number: z.string(),
    title: z.string().nullable(),
    status: z.literal('DRAFT_PO'),
  }),
  params: z.object({
    po_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postRestoreSchema = z.object(postRestore);
export type PostRestore = z.infer<typeof postRestoreSchema>;

export const deletePurchaseOrder = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const deletePurchaseOrderSchema = z.object(deletePurchaseOrder);
export type DeletePurchaseOrder = z.infer<typeof deletePurchaseOrderSchema>;

const postStatus = {
  body: z.object({
    status: z.enum(purchaseOrderStatusEnums?.map((status) => status?.value)),
  }),
  response: z.object({
    detail: z.string(),
    po_id: z.number(),
    project_po_number: z.string().nullable(),
    po_number: z.string(),
    title: z.string().nullable(),
    status: purchaseOrderStatusSchema,
  }),
  params: z.object({
    po_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postStatusSchema = z.object(postStatus);
export type PostStatus = z.infer<typeof postStatusSchema>;

const postReceive = {
  body: z.object({
    received_date: z
      .string()
      .nonempty({ error: 'Received date is required' })
      .refine((val) => dayjs(val).isValid(), { error: 'Select a valid date.' }),
    received_notes: z.string().optional().nullable(),
  }),
  response: purchaseOrderResponseSchema,
  params: z.object({
    po_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postReceiveSchema = z.object(postReceive);
export type PostReceive = z.infer<typeof postReceiveSchema>;

const deleteReceive = {
  body: z.never(),
  response: purchaseOrderResponseSchema,
  params: z.object({
    po_id: z.coerce.number(),
  }),
  query: z.never(),
};
const deleteReceiveSchema = z.object(deleteReceive);
export type DeleteReceive = z.infer<typeof deleteReceiveSchema>;

const getExpectedReceipts = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    po_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getExpectedReceiptsSchema = z.object(getExpectedReceipts);
export type GetExpectedReceipts = z.infer<typeof getExpectedReceiptsSchema>;

export const exportPdf = {
  body: z.object({
    merch: z.coerce.number().optional().nullable(),
    freight: z.coerce.number().optional().nullable(),
    tax: z.coerce.number().optional().nullable(),
    currency: z.string().optional().nullable(),
    notes_footer: z.string().optional().nullable(),

    po_date: z.string().optional().nullable(),
    ship_date: z.string().optional().nullable(),
    ship_via: z.string().optional().nullable(),

    to_company_override: z.string().optional().nullable(),
    to_address_override: z.string().optional().nullable(),
    attn: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),

    ship_to_name: z.string().optional().nullable(),
    ship_to_address: z.string().optional().nullable(),

    vendor_quote: z.string().optional().nullable(),
    function_no: z.string().optional().nullable(),

    mass_sales_tax_exempt: z.boolean().optional(),
    taxable: z.boolean().optional(),
    prepaid: z.boolean().optional(),
    allowed: z.boolean().optional(),
    collect: z.boolean().optional(),

    by_name: z.string().optional().nullable(),
    by_title: z.string().optional().nullable(),

    invoice_to_email: z.string().optional().nullable(),
    primary_contact_name: z.string().optional().nullable(),
    primary_contact_email: z.string().optional().nullable(),
  }),

  // PDF bytes (handled as Blob on frontend).
  response: z.any(),
  params: z.object({
    po_id: z.coerce.number(),
  }),
  query: z.never(),
};
const exportPdfSchema = z.object(exportPdf);
export type ExportPdf = z.infer<typeof exportPdfSchema>;

export const exportLog = {
  body: z.object({
    project_id: z.coerce.number().int().positive(),
    rows: z
      .array(
        z.object({
          purchase_order_id: z.coerce.number().int().positive(),
        }),
      )
      .min(1)
      .max(500),
  }),
  response: z.any(),
  params: z.never(),
  query: z.never(),
};
const exportLogSchema = z.object(exportLog);
export type ExportLog = z.infer<typeof exportLogSchema>;

export const getPurchaseOrdersByPart = {
  body: z.never(),
  response: z
    .object({
      id: z.number(),
      project_po_number: z.string().nullable(),
      po_number: z.string(),
      title: z.string().nullable(),
      supplier_name: z.string(),
    })
    .array(),
  params: z.never(),
  query: z
    .object({
      part_number: z.string().optional(),
      project_id: z.number().optional(),
    })
    .optional(),
};
const getPurchaseOrdersByPartSchema = z.object(getPurchaseOrdersByPart);
export type GetPurchaseOrdersByPart = z.infer<
  typeof getPurchaseOrdersByPartSchema
>;

// IMPORT WIZARD
// POST-> /api/procurement/purchase-orders/import/preview/
const postImportPreview = {
  body: z.object({
    file_url: z.string().nonempty({ error: 'File url is required' }),
    file_name: z.string().optional(),
    project: z.number(),
  }),
  response: z.object({
    import_id: z.number(),
    project_id: z.number(),
    parser: z.string().nullable(),
    fields: z.record(
      z.string(),
      z.object({
        value: z.any().nullable(),
        confidence: z.number(),
        raw: z.string().optional(),
      }),
    ),
    line_items: z
      .object({
        description: z.string().nullable(),
        part_number: z.string().nullable(),
        quantity: z.number().nullable(),
        unit: z.string().nullable(),
        unit_cost: z.number().nullable(),
        total_cost: z.number().nullable(),
        is_total_line: z.boolean().optional(),
        confidence: z.number().optional(),
      })
      .array(),
    duplicate: z.object({
      is_duplicate: z.boolean(),
      existing_po_id: z.number().nullable(),
      existing_status: z.string().nullable(),
      can_update: z.boolean(),
    }),
    error: z.string().nullable().optional(),
    raw_text_excerpt: z.string().optional(),
  }),
  params: z.never(),
  query: z.never(),
};
const postImportPreviewSchema = z.object(postImportPreview);
export type PostImportPreview = z.infer<typeof postImportPreviewSchema>;

// POST-> /api/procurement/purchase-orders/import/commit/
export const postImportCommit = {
  body: z.object({
    import_id: z.number(),
    project_id: z.number().optional(),
    mode: z.enum(['create', 'skip', 'update']).optional(),
    title: z.string().optional().nullable(),
    po_number: z.string().optional().nullable(),
    supplier_id: z.number().optional().nullable(),
    supplier_name: z.string().optional().nullable(),
    expected_delivery_date: z.string().optional().nullable(),
    shipping_address: z.string().optional().nullable(),
    function_number: z.string().optional().nullable(),
    reference_number: z.string().optional().nullable(),
    line_items: z
      .object({
        description: z.string().optional().nullable(),
        part_number: z.string().optional().nullable(),
        quantity: z.number().optional().nullable(),
        unit: z.string().optional().nullable(),
        unit_cost: z.number().optional().nullable(),
        total_cost: z.number().optional().nullable(),
        is_total_line: z.boolean().optional(),
        include: z.boolean().optional(),
      })
      .array()
      .optional(),
  }),
  response: z.object({
    action: z.string(),
    po_id: z.number(),
    project_po_number: z.string().nullable(),
    po_number: z.string(),
    title: z.string().nullable(),
    lines_created: z.number().optional(),
  }),
  params: z.never(),
  query: z.never(),
};
const postImportCommitSchema = z.object(postImportCommit);
export type PostImportCommit = z.infer<typeof postImportCommitSchema>;

// INVOICES
// POST-> /api/procurement/purchase-orders/{po_id}/invoices/
export const postPurchaseOrderInvoice = {
  body: z.object({
    file: z.url().nonempty({ error: 'File url is required' }),
    filename: z.string().nonempty({ error: 'Filename is required' }),
    invoice_number: z
      .string()
      .nonempty({ error: 'Invoice number is required' }),
    invoice_date: z
      .string()
      .nonempty({ error: 'Invoice date is required' })
      .refine((val) => dayjs(val).isValid(), { error: 'Select a valid date' }), //'2026-02-19'
    due_date: z
      .string()
      .refine((val) => val.trim() === '' || dayjs(val).isValid(), {
        error: 'Select a valid date',
      }), //'2026-02-19'
    subtotal_amount: z
      .string()
      .nonempty({ error: 'Subtotal amount is required' }),
    tax_amount: z.string(),
    freight_amount: z.string(),
    total_amount: z.string().nonempty({ error: 'Total amount is required' }),
    notes: z.string(),
  }),
  response: z.object({
    id: z.number(),
    file: z.string(),
    file_url: z.string(),
    uploaded_by: z.object({
      id: z.number(),
      display_name: z.string(),
    }),
    filename: z.string(),
    uploaded_at: z.date(), //'2026-02-19T07:15:23.100Z'
    invoice_number: z.string(),
    invoice_date: z.date(), // 2026-02-19
    due_date: z.date(), // 2026-02-19
    subtotal_amount: z.string(),
    tax_amount: z.string(),
    freight_amount: z.string(),
    total_amount: z.string(),
    notes: z.string(),
    po: z.number(),
  }),
  params: z.object({
    po_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postPurchaseOrderInvoiceSchema = z.object(postPurchaseOrderInvoice);
export type PostPurchaseOrderInvoice = z.infer<
  typeof postPurchaseOrderInvoiceSchema
>;

// GET-> /api/procurement/purchase-orders/{po_id}/invoices/
const getPurchaseOrderInvoicesAll = {
  body: z.never(),
  response: z
    .object({
      id: z.number(),
      file: z.string(),
      file_url: z.string(),
      uploaded_by: z.object({
        id: z.number(),
        display_name: z.string(),
      }),
      filename: z.string(),
      uploaded_at: z.date(), //'2026-02-19T07:15:23.100Z'
      invoice_number: z.string(),
      invoice_date: z.date(), // 2026-02-19
      due_date: z.date(), // 2026-02-19
      subtotal_amount: z.string(),
      tax_amount: z.string(),
      freight_amount: z.string(),
      total_amount: z.string(),
      notes: z.string(),
      po: z.number(),
    })
    .array(),
  params: z.object({
    po_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getPurchaseOrderInvoicesAllSchema = z.object(getPurchaseOrderInvoicesAll);
export type GetPurchaseOrderInvoicesAll = z.infer<
  typeof getPurchaseOrderInvoicesAllSchema
>;

// GET-> /api/procurement/purchase-orders/{po_id}/invoices/{invoice_id}/
const getPurchaseOrderInvoice = {
  body: z.never(),
  response: z.object({
    id: z.number(),
    file: z.string(),
    file_url: z.string(),
    uploaded_by: z.object({
      id: z.number(),
      display_name: z.string(),
    }),
    filename: z.string(),
    uploaded_at: z.date(), //'2026-02-19T07:15:23.100Z'
    invoice_number: z.string(),
    invoice_date: z.date(), // 2026-02-19
    due_date: z.date(), // 2026-02-19
    subtotal_amount: z.string(),
    tax_amount: z.string(),
    freight_amount: z.string(),
    total_amount: z.string(),
    notes: z.string(),
    po: z.number(),
  }),
  params: z.object({
    po_id: z.coerce.number(),
    invoice_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getPurchaseOrderInvoiceSchema = z.object(getPurchaseOrderInvoice);
export type GetPurchaseOrderInvoice = z.infer<
  typeof getPurchaseOrderInvoiceSchema
>;

// PATCH-> /api/procurement/purchase-orders/{po_id}/invoices/{invoice_id}/
export const patchPurchaseOrderInvoice = {
  body: postPurchaseOrderInvoice.body.partial(),
  response: z.object({
    id: z.number(),
    file: z.string(),
    file_url: z.string(),
    uploaded_by: z.object({
      id: z.number(),
      display_name: z.string(),
    }),
    filename: z.string(),
    uploaded_at: z.date(), //'2026-02-19T07:15:23.100Z'
    invoice_number: z.string(),
    invoice_date: z.date(), // 2026-02-19
    due_date: z.date(), // 2026-02-19
    subtotal_amount: z.string(),
    tax_amount: z.string(),
    freight_amount: z.string(),
    total_amount: z.string(),
    notes: z.string(),
    po: z.number(),
  }),
  params: z.object({
    po_id: z.coerce.number(),
    invoice_id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchPurchaseOrderInvoiceSchema = z.object(patchPurchaseOrderInvoice);
export type PatchPurchaseOrderInvoice = z.infer<
  typeof patchPurchaseOrderInvoiceSchema
>;

// DELETE-> /api/procurement/purchase-orders/{po_id}/invoices/{invoice_id}/
const deletePurchaseOrderInvoice = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    po_id: z.coerce.number(),
    invoice_id: z.coerce.number(),
  }),
  query: z.never(),
};
const deletePurchaseOrderInvoiceSchema = z.object(deletePurchaseOrderInvoice);
export type DeletePurchaseOrderInvoice = z.infer<
  typeof deletePurchaseOrderInvoiceSchema
>;
